import { __assign, __awaiter, __generator, __spreadArrays } from "tslib";
import { addClass, createPredicate, debounce, findWhere, getPropertyValue, isDefined, isFunction, isNumber, isObject, isString, removeAllChildren, removeClass, removeElement, replaceElement, safeCssWidth } from '@tyler-components-web/core';
import { CHECKBOX_CONSTANTS } from '../checkbox';
import { AbstractComponentDelegate, toggleClass } from '../core';
import { EXPANSION_PANEL_CONSTANTS } from '../expansion-panel';
import { PopupPlacement } from '../popup';
import { TOOLTIP_CONSTANTS } from '../tooltip';
import { TABLE_CONSTANTS } from './table-constants';
import { CellAlign, SortDirection } from './types';
import { ICON_CONSTANTS } from '../icon';
function isTemplateResultObject(val) {
    return val && typeof val === 'object' && 'content' in val;
}
/**
 * Provides facilities for creating and manipulating a table component.
 */
var TableUtils = /** @class */ (function () {
    function TableUtils() {
    }
    /**
     * Creates a table using the provided configuration.
     * @param {ITableConfiguration} configuration The table configuration.
     */
    TableUtils.createTable = function (configuration) {
        // Reset the table back to its original unpopulated state
        TableUtils._resetTable(configuration.tableElement);
        // Create the header and body rows
        var thead = TableUtils._createTableHead(configuration);
        var tbody = TableUtils._createTableBody(configuration);
        // If we are allowing click events on the table rows then attach the row click listeners and attributes
        if (configuration.clickListener) {
            TableUtils._attachRowClickListeners(tbody, configuration.clickListener, configuration.doubleClickListener);
            TableUtils._addRowClickAttributes(tbody);
        }
        // Set the fixed state
        if (configuration.fixedHeaders) {
            TableUtils.setFixedHeaders(configuration);
        }
        // Set the resizable state
        if (configuration.resizable) {
            TableUtils.setResizable(configuration);
        }
        // Set the dense state
        if (configuration.dense) {
            TableUtils.setDenseState(configuration.tableElement, configuration.dense);
        }
        // Set the wrap state
        if (configuration.wrapContent) {
            TableUtils.setWrapContentState(configuration.tableElement, configuration.wrapContent);
        }
        // Add the select column
        if (configuration.selectListener) {
            TableUtils._addSelectColumn(thead, tbody, configuration.selectListener, configuration.selectAllListener, configuration.selectAllTemplate, configuration.selectCheckboxAlignment, configuration.data, configuration.tooltipSelect, configuration.tooltipSelectAll);
        }
        if (configuration.resizable || configuration.columnConfigurations.some(function (c) { return !!c.sortable; })) {
            TableUtils._attachHeadRowMouseDownListener(thead, configuration.headRowMouseDownListener);
            if (TableUtils._isMac()) {
                TableUtils._attachHeadRowContextMenuListener(thead, configuration.headRowContextMenuListener);
            }
        }
        TableUtils.setLayoutType(configuration);
        TableUtils._setTableHead(configuration.tableElement, thead);
        // Add the filter row (must come after adding the select column and table head)
        if (configuration.filter) {
            TableUtils.setFilterRow(configuration);
        }
        TableUtils._setTableBody(configuration.tableElement, tbody);
    };
    TableUtils._setTableHead = function (tableElement, thead) {
        if (tableElement.tHead) {
            replaceElement(thead, tableElement.tHead);
        }
        else {
            tableElement.appendChild(thead);
        }
    };
    TableUtils._setTableBody = function (tableElement, tbody) {
        if (tableElement.tBodies.length) {
            replaceElement(tbody, tableElement.tBodies[0]);
        }
        else {
            tableElement.appendChild(tbody);
        }
    };
    /**
     * Destroys and recreates the table body section only.
     * @param {ITableConfiguration} configuration The table configuration.
     */
    TableUtils.recreateTableBody = function (configuration) {
        // Create the table body
        var tbody = TableUtils._createTableBody(configuration);
        // If we are allowing click events on the table rows then attach the row click listeners
        if (configuration.clickListener) {
            TableUtils._attachRowClickListeners(tbody, configuration.clickListener, configuration.doubleClickListener);
        }
        // Add the select column if necessary
        if (configuration.selectListener) {
            TableUtils._createBodySelectColumn(tbody, configuration.selectCheckboxAlignment, configuration.data, configuration.tooltipSelect, configuration.tooltipSelectAll);
            TableUtils._attachRowSelectListeners(tbody, configuration.selectListener);
        }
        TableUtils._setTableBody(configuration.tableElement, tbody);
    };
    /**
     * Removes all DOM nodes from the table.
     * @param {HTMLTableElement} tableElement The table element to remove all children from.
     */
    TableUtils._resetTable = function (tableElement) {
        removeAllChildren(tableElement);
    };
    /**
     * Creates a `tcw-tooltip` for multi sort column headers
     */
    TableUtils._createMultisortTooltip = function () {
        var tooltip = document.createElement(TOOLTIP_CONSTANTS.elementName);
        tooltip.textContent = 'Ctrl + click to sort multiple columns';
        tooltip.position = PopupPlacement.Bottom;
        tooltip.delay = 0;
        return tooltip;
    };
    /**
     * Creates the table header section by adding a row for the column headers based on column configuration.
     * @param columnDataMap The column based data map.
     * @param tableElement The table element.
     */
    TableUtils._createTableHead = function (tableConfiguration) {
        var thead = document.createElement('thead');
        // Create the table head row for our column headers with required class
        var tr = thead.insertRow();
        addClass([TABLE_CONSTANTS.classes.TABLE_ROW, TABLE_CONSTANTS.classes.TABLE_HEAD_ROW], tr);
        // We use this to determine if the initial sort column has already been set during the loop.
        // We set the initial sort to the first column that requests it.
        var setInitialSort = false;
        var _loop_1 = function (i) {
            var columnConfig = tableConfiguration.columnConfigurations[i];
            // Create the th element with required classes
            var th = document.createElement('th');
            th.scope = 'col';
            addClass([TABLE_CONSTANTS.classes.TABLE_CELL, TABLE_CONSTANTS.classes.TABLE_HEAD_CELL], th);
            // We wrap the header text in a div for ease of alignment
            var cellContainer = document.createElement('div');
            cellContainer.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_CONTAINER);
            // Add tooltip for multisort
            if (tableConfiguration.multiColumnSort) {
                var tooltip = TableUtils._createMultisortTooltip();
                cellContainer.appendChild(tooltip);
            }
            // Set the cell alignment from config
            if (columnConfig.align) {
                TableUtils._setCellAlignmentClass(cellContainer, columnConfig.align);
            }
            // Check if width was specified
            if (isDefined(columnConfig.width)) {
                var width = safeCssWidth(columnConfig.width);
                if (width) {
                    th.style.width = width;
                }
            }
            // Check if the resizable column handle should be appended
            if (tableConfiguration.resizable && columnConfig.resizable !== false) {
                var resizeHandle = document.createElement('div');
                resizeHandle.classList.add(TABLE_CONSTANTS.classes.TABLE_RESIZE_HANDLE);
                th.appendChild(resizeHandle);
            }
            // Check if we were provided any inline style declarations and apply to th AND wrapper content div
            if (isDefined(columnConfig.headerCellStyle) && isObject(columnConfig.headerCellStyle)) {
                Object.assign(th.style, columnConfig.headerCellStyle);
                Object.assign(cellContainer.style, columnConfig.headerCellStyle);
            }
            var span = document.createElement('span');
            span.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_TEXT);
            span.textContent = columnConfig.header && typeof columnConfig.header === 'string' ? columnConfig.header.trim() : '';
            // Add the sort icon if this column is sortable
            if (columnConfig.sortable) {
                th.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTABLE);
                var sortOrderElement = document.createElement('span');
                addClass([TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ORDER_HIDDEN, TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ORDER], sortOrderElement);
                var iconElement = document.createElement(ICON_CONSTANTS.elementName);
                iconElement.name = TABLE_CONSTANTS.icons.SORT_DOWN;
                addClass([TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON], iconElement);
                // Ignore the initial sort when multi column sort is enabled
                if (tableConfiguration.multiColumnSort) {
                    if (typeof columnConfig.initialSort === 'object' && columnConfig.initialSort.sortOrder) {
                        sortOrderElement.textContent = columnConfig.initialSort.sortOrder.toString();
                        if (tableConfiguration.columnConfigurations.filter(function (c) { return typeof c.initialSort === 'object'; }).length > 1) {
                            removeClass(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ORDER_HIDDEN, sortOrderElement);
                        }
                        th.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING);
                        iconElement.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON_ACTIVE);
                        TableUtils._setColumnSortDirection(th, columnConfig.initialSort.direction);
                    }
                }
                else {
                    if (tableConfiguration.sortedColumn === i && !setInitialSort && !tableConfiguration.multiColumnSort) {
                        th.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING);
                        iconElement.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON_ACTIVE);
                        TableUtils._setColumnSortDirection(th, tableConfiguration.sortDirection);
                        setInitialSort = true;
                    }
                }
                cellContainer.appendChild(iconElement);
                cellContainer.appendChild(sortOrderElement);
            }
            if (typeof columnConfig.headerTemplate === 'function') {
                Promise.resolve(columnConfig.headerTemplate(i, cellContainer, columnConfig)).then(function (element) {
                    if (element) {
                        var node = document.createElement('div');
                        addClass(TABLE_CONSTANTS.classes.TABLE_CELL_CONTAINER, node);
                        if (typeof element === 'string') {
                            node.innerHTML = element;
                        }
                        else {
                            TableUtils._prepend(element, node);
                        }
                        node.setAttribute(TABLE_CONSTANTS.attributes.CUSTOM_CELL_TEMPLATE, '');
                        TableUtils._prepend(node, cellContainer);
                    }
                });
            }
            else {
                TableUtils._prepend(span, cellContainer);
            }
            th.appendChild(cellContainer);
            tr.appendChild(th);
        };
        // Create a header cell for each column in our column data map
        for (var i = 0; i < tableConfiguration.columnConfigurations.length; i++) {
            _loop_1(i);
        }
        return thead;
    };
    /**
     * Sets the sort direction on the table to the provided column.
     * @param tableElement
     * @param columnIndex
     * @param sortDirection
     */
    TableUtils.setSortDirection = function (tableElement, columnIndex, sortDirection) {
        var cell = TableUtils._getHeaderCellByIndex(tableElement, columnIndex);
        TableUtils._setColumnSortDirection(cell, sortDirection);
    };
    /**
     * Sets the sort direction on the provided table header cell element.
     * @param thElement
     * @param sortDirection
     */
    TableUtils._setColumnSortDirection = function (thElement, sortDirection) {
        if (thElement.classList.contains(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_ASCENDING)) {
            thElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_ASCENDING);
        }
        if (thElement.classList.contains(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING)) {
            thElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING);
        }
        if (!sortDirection || sortDirection === SortDirection.Descending) {
            thElement.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING);
        }
        else {
            thElement.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_ASCENDING);
        }
    };
    /**
     * Creates the table body section by adding rows/cells for our data/column configuration.
     * @param columnDataMap The column based data map.
     * @param tableElement The table element.
     */
    TableUtils._createTableBody = function (configuration) {
        var tbody = document.createElement('tbody');
        var rowData = TableUtils._getOrderedRowData(configuration.columnConfigurations, configuration.data);
        // Create the rows
        rowData.forEach(function (cellData, rowIndex) {
            var tr = tbody.insertRow();
            addClass([TABLE_CONSTANTS.classes.TABLE_ROW, TABLE_CONSTANTS.classes.TABLE_BODY_ROW], tr);
            // Create the row data cells
            TableUtils._populateRowCells(configuration, tr, cellData, rowIndex);
            // Check if we need to call the row created callback to give consumers a chance to modify the new row element
            if (typeof configuration.rowCreatedCallback === 'function') {
                configuration.rowCreatedCallback(tr, rowIndex, __assign({}, configuration.data[rowIndex].data));
            }
        });
        return tbody;
    };
    /**
     * Populates a table row element with provided data.
     * @param configuration
     * @param tr
     * @param cellData
     * @param rowIndex
     */
    TableUtils._populateRowCells = function (configuration, tr, cellData, rowIndex) {
        var _loop_2 = function (i) {
            // Find the configuration for this column
            var columnConfig = configuration.columnConfigurations[i];
            // Create the row data cell with required classes
            var td = tr.insertCell();
            addClass([TABLE_CONSTANTS.classes.TABLE_CELL, TABLE_CONSTANTS.classes.TABLE_BODY_CELL], td);
            // Check if width was specified
            if (isDefined(columnConfig.width)) {
                var width = safeCssWidth(columnConfig.width);
                if (width) {
                    td.style.width = width;
                }
            }
            // We wrap the value in a div to allow for flex styling
            var div = document.createElement('div');
            div.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_CONTAINER);
            // Check if we were provided any inline style declarations and apply to BOTH td and content wrapper div
            if (isDefined(columnConfig.cellStyle) && isObject(columnConfig.cellStyle)) {
                Object.assign(td.style, columnConfig.cellStyle);
                Object.assign(div.style, columnConfig.cellStyle);
            }
            // Add the cell content. If there is a template function, then use that.
            // Otherwise use the property to go get the value from the row data...
            if (columnConfig.template && typeof columnConfig.template === 'function') {
                var rowData = configuration.data[rowIndex] ? configuration.data[rowIndex].data : undefined;
                Promise.resolve(columnConfig.template(rowIndex, div, rowData)).then(function (result) {
                    var config = isTemplateResultObject(result) ? result : { content: result, propagateClick: columnConfig.propagateCellTemplateClick };
                    if (!config.content) {
                        return;
                    }
                    if (typeof config.content === 'string') {
                        div.innerHTML = config.content;
                    }
                    else {
                        div.appendChild(config.content);
                    }
                    div.setAttribute(TABLE_CONSTANTS.attributes.CUSTOM_CELL_TEMPLATE, '');
                    if (config.propagateClick) {
                        div.setAttribute(TABLE_CONSTANTS.attributes.CUSTOM_CELL_TEMPLATE_PROPAGATE, '');
                    }
                });
            }
            else if (columnConfig.property) {
                // Place the text content in a span
                var span_1 = document.createElement('span');
                span_1.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_CONTAINER_TEXT);
                div.appendChild(span_1);
                if (columnConfig.transform && typeof columnConfig.transform === 'function') {
                    Promise.resolve(columnConfig.transform(cellData[i])).then(function (value) { return (span_1.textContent = value); });
                }
                else {
                    if (cellData[i] === undefined || cellData[i] === null) {
                        span_1.textContent = '';
                    }
                    else {
                        span_1.textContent = cellData[i].toString();
                    }
                }
            }
            // Set the cell alignment from config
            if (columnConfig.align) {
                TableUtils._setCellAlignmentClass(div, columnConfig.align);
            }
            td.appendChild(div);
            // Check for column span
            if (isDefined(columnConfig.columnSpan)) {
                if (columnConfig.columnSpan === 'all') {
                    td.colSpan = cellData.length - i;
                    return out_i_1 = i, "break";
                }
                else if (typeof columnConfig.columnSpan === 'number' && columnConfig.columnSpan > 0) {
                    var colspan = columnConfig.columnSpan;
                    if (columnConfig.columnSpan > cellData.length - i) {
                        colspan = cellData.length;
                    }
                    td.colSpan = colspan;
                    i = i + (colspan - 1);
                }
            }
            // Check if we need to call the cell created callback to give consumers a chance to modify the new cell element
            if (typeof configuration.cellCreatedCallback === 'function') {
                configuration.cellCreatedCallback(td, rowIndex, i);
            }
            out_i_1 = i;
        };
        var out_i_1;
        for (var i = 0; i < cellData.length; i++) {
            var state_1 = _loop_2(i);
            i = out_i_1;
            if (state_1 === "break")
                break;
        }
    };
    /**
     * Sets the proper alignment class on the provided element.
     * @param el The element to add the class to.
     * @param align The alignment value.
     */
    TableUtils._setCellAlignmentClass = function (el, align) {
        switch (align) {
            case CellAlign.Center:
                el.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_CENTER);
                break;
            case CellAlign.Right:
                el.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_RIGHT);
                break;
        }
    };
    /**
     * Returns all non-expanded rows in a tbody.
     * @param rows All rows in the tbody.
     */
    TableUtils._getNonExpandedRows = function (rows) {
        return Array.from(rows).filter(function (row) { return !row.classList.contains(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDABLE_CONTENT); });
    };
    /**
     * Returns all expanded rows in a tbody.
     * @param rows All rows in the tbody.
     */
    TableUtils._getExpandedRows = function (rows) {
        return Array.from(rows).filter(function (row) { return row.classList.contains(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDABLE_CONTENT); });
    };
    /**
     * Creates the column data map which organizes the data by column.
     * @param {IColumnConfiguration[]} columnConfigurations The column configurations.
     * @param {IColumnData[]} data The row data.
     */
    TableUtils._createColumnDataMap = function (columnConfigurations, data) {
        return columnConfigurations.map(function (columnConfig) {
            return {
                config: columnConfig,
                data: data.map(function (item) {
                    if (columnConfig.property) {
                        var value = getPropertyValue(item.data, columnConfig.property);
                        return isDefined(value) ? value : null;
                    }
                    return null;
                })
            };
        });
    };
    /**
     * Returns the row data in a column ordered fashion.
     * @param columnConfigurations
     * @param data
     */
    TableUtils._getOrderedRowData = function (columnConfigurations, data) {
        var columnDataMap = TableUtils._createColumnDataMap(columnConfigurations, data);
        var rowData = [];
        for (var _i = 0, columnDataMap_1 = columnDataMap; _i < columnDataMap_1.length; _i++) {
            var columnData = columnDataMap_1[_i];
            for (var j = 0; j < columnData.data.length; j++) {
                if (!rowData[j]) {
                    rowData[j] = [];
                }
                rowData[j].push(columnData.data[j]);
            }
        }
        return rowData;
    };
    /**
     * Attaches a click listener to each row in the table.
     * @param tbodyElement
     * @param listener
     */
    TableUtils._attachRowClickListeners = function (tbodyElement, clickListener, doubleClickListener) {
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbodyElement.rows);
        nonExpandedRows.forEach(function (row) {
            row.addEventListener('click', clickListener);
            if (doubleClickListener) {
                row.addEventListener('dblclick', doubleClickListener);
            }
        });
    };
    /**
     * Sets attributes for each row in the table.
     * @param tbodyElement
     * @param clickListener
     */
    TableUtils._addRowClickAttributes = function (tbodyElement) {
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbodyElement.rows);
        nonExpandedRows.forEach(function (row) {
            row.classList.add(TABLE_CONSTANTS.classes.TABLE_BODY_ROW_CLICKABLE);
        });
    };
    /**
     * Removes attributes for each row in the table.
     * @param tbodyElement
     * @param clickListener
     */
    TableUtils._removeRowClickAttributes = function (tbodyElement) {
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbodyElement.rows);
        nonExpandedRows.forEach(function (row) {
            row.classList.remove(TABLE_CONSTANTS.classes.TABLE_BODY_ROW_CLICKABLE);
        });
    };
    /**
     * Attaches a click listener to each row checkbox in the table for handling selections.
     * @param tbodyElement
     * @param listener
     */
    TableUtils._attachRowSelectListeners = function (tbodyElement, clickListener) {
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbodyElement.rows);
        nonExpandedRows.forEach(function (row) { return TableUtils._attachRowSelectListener(row, clickListener); });
    };
    TableUtils._attachRowSelectListener = function (row, clickListener) {
        var checkboxElement = TableUtils._getCheckboxElement(row);
        checkboxElement.addEventListener('click', clickListener);
    };
    /**
     * Removes the select listeners from every table row.
     * @param tbodyElement The table body element.
     * @param listener The click listener.
     */
    TableUtils._detachRowSelectListeners = function (tbodyElement, clickListener) {
        Array.from(tbodyElement.rows).forEach(function (row) {
            var checkboxElement = TableUtils._getCheckboxElement(row);
            if (checkboxElement) {
                checkboxElement.removeEventListener('click', clickListener);
            }
        });
    };
    /**
     * Removes the click listeners from every table row.
     * @param tbodyElement The table body element.
     * @param listener The click listener.
     */
    TableUtils._detachRowClickListeners = function (tbodyElement, clickListener, doubleClickListener) {
        Array.from(tbodyElement.rows).forEach(function (row) {
            row.removeEventListener('click', clickListener);
            row.removeEventListener('dblclick', doubleClickListener);
        });
    };
    /**
     * Attaches a click listener to the last table header row select all cell.
     */
    TableUtils._attachSelectAllListener = function (theadElement, listener) {
        var lastTheadRow = theadElement.rows[theadElement.rows.length - 1];
        var checkboxElement = TableUtils._getCheckboxElement(lastTheadRow);
        if (!checkboxElement) {
            throw new Error('Checkbox element not found.');
        }
        checkboxElement.addEventListener('change', listener);
    };
    /**
     * Attaches a click listener to the last table header row select all cell.
     */
    TableUtils._tryAttachSelectAllTemplateListener = function (theadElement, listener) {
        var lastTheadRow = theadElement.rows[theadElement.rows.length - 1];
        var checkboxElement = TableUtils._tryGetCheckboxElement(lastTheadRow);
        if (checkboxElement) {
            checkboxElement.addEventListener('change', listener);
        }
    };
    /**
     * Removes the select all click listener.
     * @param theadElement The table head element.
     * @param listener The click listener.
     */
    TableUtils._detachSelectAllListener = function (theadElement, listener) {
        var lastTheadRow = theadElement.rows[theadElement.rows.length - 1];
        var checkboxElement = TableUtils._tryGetCheckboxElement(lastTheadRow);
        if (!checkboxElement) {
            return;
        }
        checkboxElement.removeEventListener('change', listener);
    };
    /**
     * Attaches a click listener to the first row of the table header to handle mouse events.
     * @param theadElement
     * @param listener
     */
    TableUtils._attachHeadRowMouseDownListener = function (theadElement, listener) {
        var firstRow = theadElement.rows[0];
        if (!firstRow) {
            throw new Error('Missing table header row. Unable to attach sort listener.');
        }
        firstRow.addEventListener('mousedown', listener);
    };
    /**
     * Attaches a click listener to the first row of the table header to handle context menu events.
     * @param theadElement
     * @param listener
     */
    TableUtils._attachHeadRowContextMenuListener = function (theadElement, listener) {
        var firstRow = theadElement.rows[0];
        if (!firstRow) {
            throw new Error('Missing table header row. Unable to attach sort listener.');
        }
        firstRow.addEventListener('contextmenu', listener);
    };
    /**
     * Creates the select column as the first column in the table.
     * @param theadElement
     * @param tbodyElement
     */
    TableUtils._createSelectColumn = function (theadElement, tbodyElement, showSelectAll, selectAllTemplate, registerListener, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll) {
        if (theadElement) {
            TableUtils._createHeadSelectColumn(theadElement, showSelectAll, selectAllTemplate, registerListener, selectCheckboxAlignment, tooltipSelectAll);
        }
        if (tbodyElement) {
            TableUtils._createBodySelectColumn(tbodyElement, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll);
        }
    };
    /**
     * Creates the select column in the table head.
     * @param {HTMLTableSectionElement} theadElement The table head element.
     * @param {boolean} showSelectAll Whether to show the select all checkbox or not.
     */
    TableUtils._createHeadSelectColumn = function (theadElement, showSelectAll, selectAllTemplate, registerListener, selectCheckboxAlignment, tooltipSelectAll) {
        Array.from(theadElement.rows).forEach(function (row) {
            var th = document.createElement('th');
            addClass([TABLE_CONSTANTS.classes.TABLE_CELL, TABLE_CONSTANTS.classes.TABLE_HEAD_CELL], th);
            row.insertBefore(th, row.cells.item(0));
        });
        if (showSelectAll) {
            var lastRowFirstCell = theadElement.rows.item(theadElement.rows.length - 1).cells.item(0);
            lastRowFirstCell.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT);
            if (typeof selectAllTemplate === 'function') {
                TableUtils._renderSelectAllTemplate(selectAllTemplate, theadElement).then(function () {
                    if (typeof registerListener === 'function') {
                        registerListener();
                    }
                });
            }
            else {
                lastRowFirstCell.appendChild(TableUtils._createCheckboxElement(true, selectCheckboxAlignment, null, null, null, tooltipSelectAll));
            }
        }
    };
    /**
     * Creates the select column in the table body.
     * @param {HTMLTableSectionElement} tbodyElement The table body element.
     */
    TableUtils._createBodySelectColumn = function (tbodyElement, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll) {
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbodyElement.rows);
        nonExpandedRows.forEach(function (row, rowIndex) { return TableUtils._addRowSelectColumn(row, selectCheckboxAlignment, rowIndex, data[rowIndex], tooltipSelect, tooltipSelectAll); });
        // Update the colspan on the expanded rows
        if (tbodyElement.rows.length) {
            var firstRow = tbodyElement.rows.item(0);
            if (firstRow) {
                TableUtils._updateExpandedRowColspan(tbodyElement.rows, firstRow.cells.length);
            }
        }
    };
    TableUtils._addRowSelectColumn = function (row, selectCheckboxAlignment, rowIndex, rowData, tooltipSelect, tooltipSelectAll) {
        var td = row.insertCell(0);
        addClass([TABLE_CONSTANTS.classes.TABLE_CELL, TABLE_CONSTANTS.classes.TABLE_BODY_CELL, TABLE_CONSTANTS.classes.TABLE_CELL_SELECT], td);
        td.appendChild(TableUtils._createCheckboxElement(false, selectCheckboxAlignment, rowIndex, rowData, tooltipSelect, tooltipSelectAll));
    };
    /**
     * Removes the first cell (select cell) in every row in the table head and table body elements.
     * @param {HTMLTableSectionElement} theadElement The table head element.
     * @param {HTMLTableSectionElement} tbodyElement The table body element.
     */
    TableUtils._destroySelectColumn = function (theadElement, tbodyElement) {
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbodyElement.rows);
        Array.from(theadElement.rows).forEach(function (row) { return row.removeChild(row.cells.item(0)); });
        Array.from(nonExpandedRows).forEach(function (row) { return row.removeChild(row.cells.item(0)); });
        // Update the colspan on the expanded rows
        if (tbodyElement.rows.length) {
            var firstRow = tbodyElement.rows.item(0);
            if (firstRow) {
                TableUtils._updateExpandedRowColspan(tbodyElement.rows, firstRow.cells.length);
            }
        }
    };
    /**
     * Determines if the table has the select column being rendered currently.
     */
    TableUtils._hasSelectColumn = function (theadElement, tbodyElement) {
        var tableRows = __spreadArrays(Array.from(theadElement.rows), Array.from(tbodyElement.rows));
        return tableRows.some(function (row) {
            var firstCell = row.cells.item(0);
            return firstCell.classList.contains(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT);
        });
    };
    /**
     * Creates a checkbox element for the select column.
     */
    TableUtils._createCheckboxElement = function (isHeader, alignment, rowIndex, rowData, tooltipSelect, tooltipSelectAll) {
        var checkboxContainer = document.createElement('div');
        checkboxContainer.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT_CHECKBOX_CONTAINER);
        if (alignment) {
            switch (alignment) {
                case CellAlign.Center:
                    checkboxContainer.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT_CHECKBOX_CONTAINER_ALIGN_CENTER);
                    break;
                case CellAlign.Left:
                    checkboxContainer.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT_CHECKBOX_CONTAINER_ALIGN_LEFT);
                    break;
                case CellAlign.Right:
                    checkboxContainer.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT_CHECKBOX_CONTAINER_ALIGN_RIGHT);
                    break;
            }
        }
        var checkboxElement = document.createElement(CHECKBOX_CONSTANTS.elementName);
        var checkboxInputElement = document.createElement('input');
        checkboxInputElement.type = 'checkbox';
        var tooltipText = tooltipSelect ? tooltipSelect : '';
        if (rowData && typeof (tooltipSelect) === 'function') {
            tooltipText = tooltipSelect.call(null, rowIndex, rowData.data);
        }
        var tooltip = document.createElement('tcw-tooltip');
        tooltip.position = PopupPlacement.Left;
        if (isHeader) {
            tooltip.textContent = tooltipSelectAll ? tooltipSelectAll : '';
        }
        else {
            tooltip.textContent = tooltipText;
        }
        checkboxInputElement.setAttribute('aria-label', 'Select row');
        checkboxElement.appendChild(checkboxInputElement);
        checkboxContainer.appendChild(tooltip);
        checkboxContainer.appendChild(checkboxElement);
        return checkboxContainer;
    };
    /**
     * Retrieves the checkbox element from the given table row. Used in select mode only.
     * @param rowElement
     */
    TableUtils._getCheckboxElement = function (rowElement) {
        return rowElement.querySelector(TABLE_CONSTANTS.selectors.CHECKBOX_INPUT);
    };
    /**
     * Retrieves the checkbox element from the given table row. Used in select mode only.
     * @param rowElement
     */
    TableUtils._tryGetCheckboxElement = function (rowElement) {
        return rowElement.querySelector(TABLE_CONSTANTS.selectors.CHECKBOX_INPUT) || rowElement.querySelector(TABLE_CONSTANTS.selectors.SELECT_ALL_TEMPLATE_CHECKBOX_INPUT);
    };
    /**
     * Sets the checked state of the select checkbox.
     * @param checkboxElement
     * @param isSelected
     */
    TableUtils._setSelectedCheckboxState = function (checkboxElement, isSelected) {
        if (checkboxElement) {
            checkboxElement.checked = isSelected;
        }
    };
    /**
     * Sets the indeterminate state of the select checkbox.
     * @param checkboxElement
     * @param isIndeterminate
     */
    TableUtils._setIndeterminateCheckboxState = function (checkboxElement, isIndeterminate) {
        if (checkboxElement) {
            checkboxElement.indeterminate = isIndeterminate;
        }
    };
    /**
     * Updates the selected state on the provided table row element.
     * @param rowElement
     * @param isSelected
     */
    TableUtils._setRowSelectedState = function (rowElement, isSelected) {
        if (isSelected) {
            if (!rowElement.classList.contains(TABLE_CONSTANTS.classes.TABLE_BODY_ROW_SELECTED)) {
                rowElement.classList.add(TABLE_CONSTANTS.classes.TABLE_BODY_ROW_SELECTED);
            }
        }
        else {
            rowElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_BODY_ROW_SELECTED);
        }
    };
    /**
     * Resets the colspan on all expanded rows to the proper value.
     */
    TableUtils._updateExpandedRowColspan = function (rows, cellCount) {
        var expandedRows = TableUtils._getExpandedRows(rows);
        expandedRows.forEach(function (row) {
            var cell = row.cells.item(0);
            if (cell) {
                cell.colSpan = cellCount;
            }
        });
    };
    /**
     * Updates the state of a table row element to be selected or not.
     * @param {HTMLTableRowElement} rowElement The row element.
     * @param {boolean} isSelected Whether the row is selected or not.
     */
    TableUtils.updateSelectedState = function (rowElement, isSelected) {
        TableUtils._setRowSelectedState(rowElement, isSelected);
        TableUtils._setSelectedCheckboxState(TableUtils._getCheckboxElement(rowElement), isSelected);
    };
    /**
     * Get a row from the first table body by index.
     */
    TableUtils.getRowByIndex = function (tableElement, index) {
        return TableUtils._getNonExpandedRows(tableElement.tBodies[0].rows)[index];
    };
    /**
     * Updates the state of the select all checkbox.
     * @param {HTMLTableElement} tableElement The table element.
     * @param {boolean} isAllSelected Whether the checkboxes should be checked or not.
     */
    TableUtils.updateSelectAllState = function (tableElement, isAllSelected, isIndeterminate) {
        if (!tableElement.tHead) {
            return;
        }
        var lastTheadRow = tableElement.tHead.rows[tableElement.tHead.rows.length - 1];
        var selectAllCheckboxElement = TableUtils._tryGetCheckboxElement(lastTheadRow);
        if (selectAllCheckboxElement) {
            TableUtils._setSelectedCheckboxState(selectAllCheckboxElement, isAllSelected);
            TableUtils._setIndeterminateCheckboxState(selectAllCheckboxElement, !!isIndeterminate);
        }
    };
    /**
     * Sets the selected rows in the table.
     * @param tableElement
     * @param key
     * @param data
     * @param selectedRows
     * @param preserveExisting
     */
    TableUtils.setSelectedRows = function (tableElement, key, data, selectedRows, preserveExisting) {
        if (preserveExisting === void 0) { preserveExisting = false; }
        if (!tableElement.tBodies.length) {
            return;
        }
        if (!preserveExisting) {
            TableUtils.clearSelectedRows(tableElement);
        }
        var rows = tableElement.tBodies[0].rows;
        var selectedRowCount = 0;
        if (selectedRows.length) {
            Array.from(rows).forEach(function (row, index) {
                var rowData = data[index];
                var existingSelection = findWhere(selectedRows, createPredicate(key, rowData));
                if (existingSelection) {
                    TableUtils._setRowSelectedState(row, true);
                    TableUtils._setSelectedCheckboxState(TableUtils._getCheckboxElement(row), true);
                    selectedRowCount++;
                }
            });
        }
        TableUtils.updateSelectAllState(tableElement, selectedRowCount > 0 && rows.length === selectedRowCount);
    };
    /**
     * Clears all selected rows in the table.
     * @param tableElement
     */
    TableUtils.clearSelectedRows = function (tableElement) {
        if (!tableElement.tBodies.length) {
            return;
        }
        var nonExpandedRows = TableUtils._getNonExpandedRows(tableElement.tBodies[0].rows);
        nonExpandedRows.forEach(function (row) {
            TableUtils._setRowSelectedState(row, false);
            TableUtils._setSelectedCheckboxState(TableUtils._getCheckboxElement(row), false);
        });
    };
    /**
     * Sets the sorted column in the table.
     * @param tableElement
     * @param columnIndex
     * @param sortDirection
     */
    TableUtils.setSortedColumn = function (tableElement, columnIndex, sortDirection) {
        var cell = TableUtils._getHeaderCellByIndex(tableElement, columnIndex);
        // Set the active class on the icon
        var iconElement = TableUtils._getSortIconElementFromHeaderCell(cell);
        iconElement.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON_ACTIVE);
        // Set the sort direction on the header cell
        TableUtils._setColumnSortDirection(cell, sortDirection);
    };
    /**
     * Sets the sorted column in the table.
     * @param tableElement
     * @param columnIndex
     * @param sortOrder
     */
    TableUtils.setMultiSortColumnSortNumber = function (tableElement, hasMultipleColumnsSorted, columnIndex, sortOrder) {
        var cell = TableUtils._getHeaderCellByIndex(tableElement, columnIndex);
        var cellContainer = cell.querySelector("." + TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_CONTAINER);
        var sortOrderElement = cellContainer.querySelector("." + TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ORDER);
        if (sortOrderElement) {
            toggleClass(sortOrderElement, typeof sortOrder !== 'number' || !hasMultipleColumnsSorted, TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ORDER_HIDDEN);
            if (sortOrder) {
                sortOrderElement.textContent = sortOrder.toString();
            }
        }
    };
    /**
     * Removes the sorted column in the table.
     * @param tableElement
     * @param columnIndex
     */
    TableUtils.removeColumnSort = function (tableElement, columnIndex) {
        var cell = TableUtils._getHeaderCellByIndex(tableElement, columnIndex);
        // Remove any existing sort direction classes from the existing th element
        if (cell.classList.contains(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_ASCENDING)) {
            cell.classList.remove(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_ASCENDING);
        }
        if (cell.classList.contains(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING)) {
            cell.classList.remove(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORTED_DESCENDING);
        }
        // Remove the active class from the existing active sorted icon element
        var existingIconElement = TableUtils._getSortIconElementFromHeaderCell(cell);
        if (existingIconElement && existingIconElement.classList.contains(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON_ACTIVE)) {
            existingIconElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON_ACTIVE);
        }
    };
    /**
     * Retrieves a header cell from the table based on index.
     * @param {HTMLTableElement} tableElement The table element.
     * @param {number} index The column index.
     */
    TableUtils._getHeaderCellByIndex = function (tableElement, index) {
        if (!tableElement.tHead) {
            throw new Error('Table head element cannot be null.');
        }
        var headerRow = tableElement.tHead.rows.item(0);
        return headerRow.cells.item(index);
    };
    /**
     * Gets the sort icon element from the table cell that contains the select all checkbox.
     * @param {HTMLTableHeaderCellElement} cell The table header cell element.
     */
    TableUtils._getSortIconElementFromHeaderCell = function (cell) {
        return cell.querySelector("." + TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_SORT_ICON);
    };
    /**
     * Adds/removes the select column on the table.
     */
    TableUtils.setSelectColumnVisibility = function (tableElement, isVisible, selectListener, selectAllListener, selectAllTemplate, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll) {
        var theadElement = tableElement.tHead;
        var tbodyElement = tableElement.tBodies[0];
        if (!theadElement || !tbodyElement) {
            return;
        }
        if (isVisible) {
            TableUtils._addSelectColumn(theadElement, tbodyElement, selectListener, selectAllListener, selectAllTemplate, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll);
        }
        else if (TableUtils._hasSelectColumn(theadElement, tbodyElement)) {
            if (selectListener) {
                TableUtils._detachRowSelectListeners(tbodyElement, selectListener);
            }
            if (selectAllListener) {
                TableUtils._detachSelectAllListener(theadElement, selectAllListener);
            }
            TableUtils._destroySelectColumn(theadElement, tbodyElement);
        }
    };
    /**
     * Adds the select column to the thead/tbody elements and attaches the provided click listeners.
     * @param theadElement The table head element.
     * @param tbodyElement The table body element.
     * @param selectListener The row select listener.
     * @param selectAllListener The select all checkbox listener.
     */
    TableUtils._addSelectColumn = function (theadElement, tbodyElement, selectListener, selectAllListener, selectAllTemplate, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll) {
        TableUtils._createSelectColumn(theadElement, tbodyElement, !!selectAllListener, selectAllTemplate, function () {
            if (selectAllListener) {
                TableUtils._tryAttachSelectAllTemplateListener(theadElement, selectAllListener);
            }
        }, selectCheckboxAlignment, data, tooltipSelect, tooltipSelectAll);
        if (selectListener) {
            TableUtils._attachRowSelectListeners(tbodyElement, selectListener);
        }
        if (selectAllListener && !selectAllTemplate) {
            TableUtils._tryAttachSelectAllTemplateListener(theadElement, selectAllListener);
        }
    };
    /**
     * Adds/removes the dense class on the table.
     */
    TableUtils.setDenseState = function (tableElement, isDense) {
        if (tableElement.classList.contains(TABLE_CONSTANTS.classes.TABLE_DENSE)) {
            tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_DENSE);
        }
        if (isDense) {
            tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_DENSE);
        }
    };
    /**
     * Adds/removes the roomy class on the table.
     */
    TableUtils.setRoomyState = function (tableElement, isRoomy) {
        if (tableElement.classList.contains(TABLE_CONSTANTS.classes.TABLE_ROOMY)) {
            tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_ROOMY);
        }
        if (isRoomy) {
            tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_ROOMY);
        }
    };
    TableUtils.setResizable = function (configuration) {
        var thead = configuration.tableElement.tHead;
        if (configuration.resizable) {
            configuration.tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_RESIZABLE);
            TableUtils._addResizeHandles(thead, configuration);
        }
        else {
            configuration.tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_RESIZABLE);
            TableUtils._removeResizeHandles(thead);
        }
    };
    TableUtils._addResizeHandles = function (thead, configuration) {
        if (!thead) {
            return;
        }
        var firstRow = thead.rows.item(0);
        if (firstRow) {
            var cells = Array.from(firstRow.cells);
            // If the select column is on, we need to skip the first cell
            if (configuration.selectListener) {
                cells = cells.slice(1);
            }
            for (var _i = 0, cells_1 = cells; _i < cells_1.length; _i++) {
                var cell = cells_1[_i];
                var index = cells.indexOf(cell);
                var columnConfig = configuration.columnConfigurations[index];
                if (columnConfig.resizable !== false) {
                    var resizeHandle = document.createElement('div');
                    resizeHandle.classList.add(TABLE_CONSTANTS.classes.TABLE_RESIZE_HANDLE);
                    cell.appendChild(resizeHandle);
                }
            }
        }
    };
    TableUtils._removeResizeHandles = function (thead) {
        if (!thead) {
            return;
        }
        var firstRow = thead.rows.item(0);
        if (firstRow) {
            var cells = Array.from(firstRow.cells);
            for (var _i = 0, cells_2 = cells; _i < cells_2.length; _i++) {
                var cell = cells_2[_i];
                var resizeHandle = document.querySelector("." + TABLE_CONSTANTS.classes.TABLE_RESIZE_HANDLE);
                if (resizeHandle) {
                    cell.removeChild(resizeHandle);
                }
            }
        }
    };
    /**
     * Adds/removes the wrap class on the table.
     */
    TableUtils.setWrapContentState = function (tableElement, wrapContent) {
        if (wrapContent) {
            tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_NO_WRAP_CONTENT);
        }
        else {
            tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_NO_WRAP_CONTENT);
        }
    };
    /**
     *
     * @param {HTMLTableElement} tableElement The table element.
     * @param {boolean} isVisible Whether the select all option is visible or not.
     */
    TableUtils.setSelectAllVisibility = function (tableElement, isVisible, listener, selectAllTemplate, selectCheckboxAlignment, tooltipSelectAll) {
        var theadElement = tableElement.tHead;
        if (!theadElement) {
            return;
        }
        if (!isVisible && listener) {
            TableUtils._detachSelectAllListener(theadElement, listener);
        }
        var lastTheadRow = theadElement.rows[theadElement.rows.length - 1];
        var selectAllCell = lastTheadRow.cells.item(0);
        if (isVisible) {
            // Only add the checkbox if it doesn't already exist
            if (!selectAllCell.childElementCount) {
                selectAllCell.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT);
                if (selectAllTemplate) {
                    TableUtils._renderSelectAllTemplate(selectAllTemplate, theadElement).then(function () {
                        if (listener) {
                            TableUtils._tryAttachSelectAllTemplateListener(theadElement, listener);
                        }
                    });
                }
                else {
                    selectAllCell.appendChild(TableUtils._createCheckboxElement(true, selectCheckboxAlignment || null, null, null, null, tooltipSelectAll));
                }
                if (listener && !selectAllTemplate) {
                    TableUtils._attachSelectAllListener(theadElement, listener);
                }
            }
        }
        else {
            if (listener) {
                TableUtils._detachSelectAllListener(theadElement, listener);
            }
            removeAllChildren(selectAllCell);
        }
    };
    /**
     * Controls the visibility of the table filter row.
     * @param {ITableConfiguration} configuration The table configuration.
     */
    TableUtils.setFilterRow = function (configuration) {
        if (!configuration.tableElement.tHead) {
            return;
        }
        var filterRowElement = TableUtils._getFilterRowElement(configuration.tableElement.tHead);
        var selectAllCell;
        // Toggle the filter visible class on the table element to control styles of rows/cells
        if (configuration.filter) {
            configuration.tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_FILTER_VISIBLE);
        }
        else {
            configuration.tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_FILTER_VISIBLE);
        }
        if (configuration.filter && !filterRowElement) {
            // We can stop here if there are no filterable columns
            if (configuration.columnConfigurations.every(function (cc) { return !cc.filter; })) {
                return;
            }
            // Gets the select all cell contents so it can be moved to the new filter row after creating the row element
            if (configuration.selectListener && configuration.selectAllListener) {
                var lastTheadRow = configuration.tableElement.tHead.rows[configuration.tableElement.tHead.rows.length - 1];
                selectAllCell = lastTheadRow.cells.item(0);
            }
            var rowElement_1 = TableUtils._createFilterRowElement(configuration);
            // Move the select all cell to the new filter row
            if (rowElement_1 && selectAllCell) {
                Array.from(selectAllCell.children).forEach(function (child) {
                    rowElement_1.cells.item(0).appendChild(child);
                });
            }
        }
        else if (!configuration.filter && filterRowElement) {
            // Move the select all cell to the previous row
            if (configuration.selectListener && configuration.selectAllListener) {
                selectAllCell = filterRowElement.cells.item(0);
                var previousRowElementIndex = Array.from(configuration.tableElement.tHead.rows).indexOf(filterRowElement) - 1;
                var previousRowElement_1 = configuration.tableElement.tHead.rows.item(previousRowElementIndex);
                if (previousRowElement_1) {
                    Array.from(selectAllCell.children).forEach(function (child) {
                        previousRowElement_1.cells.item(0).appendChild(child);
                    });
                }
            }
            removeElement(filterRowElement);
        }
    };
    /**
     * Creates the table filter row element.
     * @param {ITableConfiguration} configuration The table configuration.
     */
    TableUtils._createFilterRowElement = function (configuration) {
        if (!configuration.tableElement.tHead) {
            throw new Error('Table head element cannot be null.');
        }
        var filterRow = configuration.tableElement.tHead.insertRow();
        addClass([TABLE_CONSTANTS.classes.TABLE_ROW, TABLE_CONSTANTS.classes.TABLE_HEAD_ROW_FILTER], filterRow);
        if (configuration.selectListener) {
            var th = document.createElement('th');
            addClass([TABLE_CONSTANTS.classes.TABLE_CELL, TABLE_CONSTANTS.classes.TABLE_HEAD_CELL], th);
            filterRow.insertBefore(th, filterRow.cells[0]);
            th.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_SELECT);
        }
        configuration.columnConfigurations.forEach(function (columnConfig, columnIndex) {
            var th = document.createElement('th');
            addClass([TABLE_CONSTANTS.classes.TABLE_CELL, TABLE_CONSTANTS.classes.TABLE_HEAD_CELL], th);
            if (columnConfig.filter && isDefined(columnConfig.filterDelegate)) {
                var container = document.createElement('div');
                container.classList.add(TABLE_CONSTANTS.classes.TABLE_HEAD_CELL_CONTAINER);
                if (columnConfig.align) {
                    TableUtils._setCellAlignmentClass(container, columnConfig.align);
                }
                var element = TableUtils._createFilterElement(columnConfig, columnIndex, configuration.filterListener);
                container.appendChild(element);
                th.appendChild(container);
            }
            filterRow.appendChild(th);
        });
        return filterRow;
    };
    /**
     * Creates the element that will be used as the filter for this column.
     * @param {IColumnConfiguration} columnConfig The column configuration.
     */
    TableUtils._createFilterElement = function (columnConfig, columnIndex, filterListener) {
        var delegate;
        if (isFunction(columnConfig.filterDelegate)) {
            delegate = columnConfig.filterDelegate();
        }
        else if (columnConfig.filterDelegate instanceof AbstractComponentDelegate) {
            delegate = columnConfig.filterDelegate;
        }
        else {
            throw new Error('Invalid filter delegate.');
        }
        if (filterListener) {
            if (!isDefined(columnConfig.filterDebounceTime) || isNumber(columnConfig.filterDebounceTime)) {
                var debounceTime = isDefined(columnConfig.filterDebounceTime) ? columnConfig.filterDebounceTime : TABLE_CONSTANTS.numbers.DEFAULT_FILTER_DEBOUNCE_TIME;
                delegate.onChange(debounce(function (value) { return filterListener(value, columnIndex); }, debounceTime));
            }
            else {
                delegate.onChange(function (value) { return filterListener(value, columnIndex); });
            }
        }
        return delegate.component;
    };
    /**
     * Gets the filter row from the table head element rows, or undefined if not found.
     * @param {HTMLTableRowElement | undefined} tHeadElement The thead element.
     */
    TableUtils._getFilterRowElement = function (tHeadElement) {
        if (!tHeadElement) {
            return;
        }
        return Array.from(tHeadElement.rows).find(function (r) { return r.classList.contains(TABLE_CONSTANTS.classes.TABLE_HEAD_ROW_FILTER); });
    };
    /**
     * Expands a row by inserting a new row beneath it and displaying the provided template content.
     * @param configuration The table configuration.
     * @param rowIndex The index of the row to expand.
     * @param template The template for the expanded row content.
     */
    TableUtils.expandRow = function (configuration, rowIndex, template) {
        return __awaiter(this, void 0, void 0, function () {
            var tbody, nonExpandedRows, requestedRow, actualRowIndex, expandedRow, td, contentDiv, expansionPanel, content, rowData, templateValue, result, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        tbody = configuration.tableElement.tBodies[0];
                        nonExpandedRows = TableUtils._getNonExpandedRows(tbody.rows);
                        requestedRow = nonExpandedRows[rowIndex];
                        requestedRow.classList.add(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDED);
                        actualRowIndex = Array.from(tbody.rows).indexOf(requestedRow);
                        expandedRow = tbody.insertRow(actualRowIndex + 1);
                        addClass([TABLE_CONSTANTS.classes.TABLE_ROW, TABLE_CONSTANTS.classes.TABLE_BODY_ROW, TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDABLE_CONTENT], expandedRow);
                        td = expandedRow.insertCell();
                        td.setAttribute('colspan', requestedRow.cells.length.toString());
                        td.classList.add(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDABLE_CONTENT_CELL);
                        contentDiv = document.createElement('div');
                        expansionPanel = document.createElement(EXPANSION_PANEL_CONSTANTS.elementName);
                        expansionPanel.appendChild(contentDiv);
                        td.appendChild(expansionPanel);
                        content = template;
                        if (!isFunction(template)) return [3 /*break*/, 4];
                        rowData = configuration.data[rowIndex] ? configuration.data[rowIndex].data : undefined;
                        templateValue = template(rowIndex, contentDiv, rowData);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, Promise.resolve(templateValue)];
                    case 2:
                        result = _a.sent();
                        content = isTemplateResultObject(result) ? result.content : result;
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        console.error('Failed to load row template ' + e_1.message);
                        content = '';
                        return [3 /*break*/, 4];
                    case 4:
                        if (content) {
                            TableUtils._setRowTemplate(contentDiv, content);
                        }
                        window.requestAnimationFrame(function () { return (expansionPanel.open = true); });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Sets the template content in the table row element.
     * @param {HTMLElement} element The element to the append the template to.
     * @param {string | HTMLElement} template The template content.
     */
    TableUtils._setRowTemplate = function (element, template) {
        if (isString(template)) {
            element.innerHTML = template;
        }
        else {
            element.appendChild(template);
        }
    };
    /**
     * Collapses an expanded table row.
     * @param configuration The table configuration.
     * @param rowIndex The index of the row to collapse.
     */
    TableUtils.collapseRow = function (configuration, rowIndex) {
        if (!configuration.tableElement || !configuration.tableElement.tBodies.length || !configuration.tableElement.tBodies[0].rows.length) {
            return Promise.resolve();
        }
        var tbody = configuration.tableElement.tBodies[0];
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbody.rows);
        var requestedRow = nonExpandedRows[rowIndex];
        var actualRowIndex = Array.from(tbody.rows).indexOf(requestedRow);
        if (requestedRow && requestedRow.classList.contains(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDED)) {
            var expandableRow_1 = tbody.rows[actualRowIndex + 1];
            var expansionPanel = expandableRow_1.querySelector(EXPANSION_PANEL_CONSTANTS.elementName);
            if (expansionPanel && expansionPanel.open) {
                expansionPanel.open = false;
                return new Promise(function (resolve) {
                    setTimeout(function () {
                        requestedRow.classList.remove(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDED);
                        removeElement(expandableRow_1);
                        resolve();
                    }, EXPANSION_PANEL_CONSTANTS.numbers.COLLAPSE_ANIMATION_DURATION);
                });
            }
        }
        return Promise.resolve();
    };
    /**
     * Checks if a row is expanded or not.
     * @param configuration The table configuration.
     * @param rowIndex The row index.
     * @returns {boolean}
     */
    TableUtils.isRowExpanded = function (configuration, rowIndex) {
        var tbody = configuration.tableElement.tBodies[0];
        var nonExpandedRows = TableUtils._getNonExpandedRows(tbody.rows);
        var requestedRow = nonExpandedRows[rowIndex];
        return requestedRow && requestedRow.classList.contains(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDED);
    };
    /**
     * Sets the fixed class on the table element based on whether fixed headers were requested or not.
     * @param configuration The table configuration.
     */
    TableUtils.setFixedHeaders = function (configuration) {
        if (configuration.fixedHeaders) {
            configuration.tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_FIXED);
        }
        else {
            configuration.tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_FIXED);
        }
    };
    /**
     * Sets the table layout algorithm.
     * @param configuration The table configuration.
     */
    TableUtils.setLayoutType = function (configuration) {
        if (configuration.layoutType === 'fixed') {
            configuration.tableElement.classList.add(TABLE_CONSTANTS.classes.TABLE_LAYOUT_FIXED);
        }
        else {
            configuration.tableElement.classList.remove(TABLE_CONSTANTS.classes.TABLE_LAYOUT_FIXED);
        }
    };
    /**
     * Ensures that any columns width styles are within the bounds of available space.
     * @param configuration The table configuration.
     */
    TableUtils.normalizeColumnWidths = function (configuration) {
        var tableElement = configuration.tableElement;
        if (tableElement.tHead && tableElement.tHead.rows.length) {
            var cells = Array.from(tableElement.tHead.rows[0].cells);
            for (var _i = 0, cells_3 = cells; _i < cells_3.length; _i++) {
                var cell = cells_3[_i];
                var expectedWidth = getComputedStyle(cell).width;
                if (cell.style.width && cell.style.width !== expectedWidth) {
                    cell.style.width = expectedWidth;
                }
            }
        }
    };
    /**
     * Toggles the visibility of the resize cell indicator for all table cells.
     * @param configuration The table configuration.
     * @param columnIndex The cell index of the column being resized.
     * @param isVisible Whether to show the indicator or not.
     */
    TableUtils.setColumnResizeIndicatorVisibility = function (configuration, columnIndex, isVisible) {
        var tableElement = configuration.tableElement;
        if (tableElement.tHead && tableElement.tHead.rows.length) {
            var rows = Array.from(tableElement.tHead.rows);
            for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
                var row = rows_1[_i];
                var cell = row.cells[columnIndex];
                if (cell) {
                    if (isVisible) {
                        cell.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_RESIZABLE);
                    }
                    else {
                        cell.classList.remove(TABLE_CONSTANTS.classes.TABLE_CELL_RESIZABLE);
                    }
                }
            }
        }
        if (tableElement.tBodies.length) {
            var rows = Array.from(tableElement.tBodies[0].rows);
            for (var _a = 0, rows_2 = rows; _a < rows_2.length; _a++) {
                var row = rows_2[_a];
                var cell = row.cells[columnIndex];
                if (cell) {
                    if (isVisible) {
                        cell.classList.add(TABLE_CONSTANTS.classes.TABLE_CELL_RESIZABLE);
                    }
                    else {
                        cell.classList.remove(TABLE_CONSTANTS.classes.TABLE_CELL_RESIZABLE);
                    }
                }
            }
        }
    };
    TableUtils.setRowClickListeners = function (tableElement, allowClick, clickListener, doubleClickListener) {
        var tbodyElement = tableElement.tBodies[0];
        if (!tbodyElement) {
            return;
        }
        if (allowClick) {
            TableUtils._attachRowClickListeners(tbodyElement, clickListener, doubleClickListener);
        }
        else {
            TableUtils._detachRowClickListeners(tbodyElement, clickListener, doubleClickListener);
        }
    };
    TableUtils.setRowClickAttributes = function (tableElement, allowClick) {
        var tbodyElement = tableElement.tBodies[0];
        if (!tbodyElement) {
            return;
        }
        if (allowClick) {
            TableUtils._addRowClickAttributes(tbodyElement);
        }
        else {
            TableUtils._removeRowClickAttributes(tbodyElement);
        }
    };
    TableUtils.getOwnerDocument = function (el) {
        return el.ownerDocument || document;
    };
    TableUtils._prepend = function (element, target) {
        var elementToSetBefore = target.childNodes.item(0);
        if (elementToSetBefore) {
            target.insertBefore(element, elementToSetBefore);
        }
        else {
            target.appendChild(element);
        }
    };
    TableUtils._isMac = function () {
        // Not checking for `MacIntel` because Apple Silicon
        return !!(navigator && navigator.platform && navigator.platform.substr(0, 3) === 'Mac');
    };
    TableUtils._renderSelectAllTemplate = function (selectAllTemplate, theadElement) {
        return Promise.resolve(selectAllTemplate()).then(function (element) {
            var lastTheadRow = theadElement.rows.item(theadElement.rows.length - 1);
            if (!lastTheadRow) {
                return;
            }
            var targetElement = lastTheadRow.cells.item(0);
            if (element && targetElement) {
                if (typeof element === 'string') {
                    targetElement.innerHTML = element;
                }
                else if (element instanceof HTMLElement) {
                    targetElement.appendChild(element);
                }
                targetElement.setAttribute(TABLE_CONSTANTS.attributes.CUSTOM_CELL_TEMPLATE, '');
            }
        });
    };
    return TableUtils;
}());
export { TableUtils };
